import { useCallback, useState } from 'react';
import { extractApiErrorMessage } from '../../utils/apiError';
import { fetchThreadFolders, type ThreadContext } from '../../api/threadTools';
import { ConversationToolbar } from './ConversationToolbar';
import {
  applyConversationTools,
  uploadRidesNextMessage,
  type ConversationToolIntent,
} from './conversationTools';
import { useCan } from '../../hooks/useCan';

/**
 * What an *open* conversation can do beyond typing into it
 * (COMMUNICATIONS-DESIGN H4).
 *
 * ⚠️ **The controls are `ConversationToolbar`'s; only the timing is here.** The
 * same three tools are offered while a conversation is being started, where
 * there is no thread to apply them to yet — so the toolbar reports a choice and
 * this applies it at once. Two copies of "attach a report" would drift, and the
 * one on the dialog nobody looks at twice would drift first.
 *
 * ⚠️ **`onChanged` is how the panel finds out.** `<ThreadPanel>` loads its own
 * attachments and exposes no reload, so the parent remounts it on a key bump
 * (see `RecordConversations`). Remounting a transcript is cheap and honest; the
 * alternative was rebuilding the panel here out of `useThread` to get at
 * `reload()`, which is a screen this application does not need to own.
 */
export interface ThreadToolsProps {
  threadId: string;
  /** Where this conversation's files live, from `GET /threads/:id/context`. */
  context: ThreadContext | null;
  /** Called after anything lands, so the transcript can pick it up. */
  onChanged: () => void;
  /**
   * Hold a file request for the next message instead of sending one now.
   *
   * ⚠️ **Because a message and an ask are one act, and used to arrive as two.**
   * Asking posted a message of its own, so the recipient got the writer's words
   * in one email and the button in another. Handed up instead, it rides on the
   * next message and arrives as a single email. A parent that does not supply
   * this keeps the old behaviour, which is what the new-conversation dialog
   * needs: there is no next message there, the first one *is* the ask.
   */
  onStageUpload?: (config: NonNullable<ConversationToolIntent['upload']>) => void;
}

export function ThreadTools({ threadId, context, onChanged, onStageUpload }: ThreadToolsProps) {
  const canAttach = useCan('conversations.attach');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);

  /**
   * How the toolbar fetches the folders an incoming file may be filed into.
   *
   * ⚠️ **Thread-keyed, not record-keyed.** `/threads/:id/folders` admits anybody
   * *on* the conversation, which is the same gate the transcript uses; the
   * record-keyed form cannot see past `mayAttachTo` and would refuse a vendor
   * reading a conversation they are legitimately part of.
   *
   * ⚠️ **Handed over rather than called here, so the walk is lazy.** This ran on
   * mount, which meant every conversation anybody *opened* walked the record's
   * folder tree — one `tree()` call per node — to fill a dialog most readers
   * never open. The toolbar knows when that dialog opens; this does not.
   *
   * ⚠️ **Memoised on the thread id**, because the toolbar treats a new function
   * as a new record and drops the folders it had cached.
   */
  const loadFolders = useCallback(() => fetchThreadFolders(threadId), [threadId]);

  /**
   * ⚠️ **Rethrown after it is reported.** The toolbar keeps its dialog open on
   * a rejection, so what was typed survives a failed send and the retry is one
   * click. Swallowing here would close the dialog over an error banner.
   */
  async function apply(intent: ConversationToolIntent): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      /*
       * The ask is held for the next message only when there is no note to send
       * — `uploadRidesNextMessage` states the rule and the toolbar's button
       * reads its label off the same call. Everything else still lands now,
       * because a file attached is a file attached and there is nothing to wait
       * for.
       *
       * ⚠️ **It used to hold *every* ask, and that quietly threw the note
       * away.** A producer typed "please send the signed contract", pressed a
       * button reading "Send the link", and nothing was sent: the request sat
       * staged until they happened to post another message, at which point the
       * ask went out carrying that message's words instead of the ones they had
       * written for it. Which reads exactly like the fault staging was built to
       * fix — the request arriving as a separate, later email.
       */
      const staged =
        onStageUpload !== undefined &&
        intent.upload !== undefined &&
        uploadRidesNextMessage(intent.upload, true);
      const rest = staged ? { ...intent, upload: undefined } : intent;
      const outcomes = await applyConversationTools(threadId, rest);
      if (staged && intent.upload !== undefined) {
        onStageUpload(intent.upload);
        outcomes.push('A file request will go out with your next message.');
      }
      if (outcomes.length > 0) setOutcome(outcomes.join(' '));
      onChanged();
    } catch (err) {
      // The server's message rather than Axios's status line — see the same
      // note in `ThreadReportModal`.
      setError(
        extractApiErrorMessage(err, err instanceof Error ? err.message : 'That did not work.'),
      );
      throw err;
    } finally {
      setBusy(false);
    }
  }

  // `conversations.attach` withdrawn: the conversation stays, the tools go.
  if (!canAttach) return null;

  return (
    <div className="mt-3 space-y-2" data-testid="thread-tools">
      {error && (
        <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </p>
      )}
      {outcome && (
        <p className="px-3 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
          {outcome}
        </p>
      )}

      <ConversationToolbar
        context={context}
        disabled={busy}
        onIntent={apply}
        deferrable={onStageUpload !== undefined}
        loadFolders={loadFolders}
      />
    </div>
  );
}
